/**
 * apps/web :: lib/languages
 *
 * The 9 MVP languages supported by the rule-diff engine + onboarding wizard.
 * Wire codes match the backend `LangCode` enum (see
 * `kmp-shared-core/src/main/kotlin/cx/humane/roadbridge/shared/profile/LangCode.kt`).
 *
 * Each entry carries:
 *   - `code`: wire-format language code (ISO 639-1 or BCP-47 script variant).
 *   - `nativeName`: display name in its own script — the primary card label.
 *   - `englishName`: transliteration / English name below in smaller type, so
 *     users who don't recognise their own script still find their language.
 *
 * Order mirrors the PRD priority: English + Spanish first (widest coverage),
 * then the four Indian-diaspora top-n (Hindi + Telugu + Tagalog), then the
 * Slavic locales, then Turkish (Wave 13), then Chinese (handbook validated).
 */

export type LangCode =
  | "en"
  | "es"
  | "hi"
  | "te"
  | "tl"
  | "ru"
  | "uk"
  | "tr"
  | "zh-Hans";

export interface Language {
  code: LangCode;
  nativeName: string;
  englishName: string;
}

export const LANGUAGES: readonly Language[] = [
  { code: "en", nativeName: "English", englishName: "English" },
  { code: "es", nativeName: "Español", englishName: "Spanish" },
  { code: "hi", nativeName: "हिन्दी", englishName: "Hindi" },
  { code: "te", nativeName: "తెలుగు", englishName: "Telugu" },
  { code: "tl", nativeName: "Tagalog", englishName: "Tagalog" },
  { code: "ru", nativeName: "Русский", englishName: "Russian" },
  { code: "uk", nativeName: "Українська", englishName: "Ukrainian" },
  { code: "tr", nativeName: "Türkçe", englishName: "Turkish" },
  { code: "zh-Hans", nativeName: "简体中文", englishName: "Simplified Chinese" },
] as const;

/** Look up a language by wire code. Returns `undefined` for unknown codes. */
export function getLanguage(code: string): Language | undefined {
  return LANGUAGES.find((l) => l.code === code);
}
